/**
 * Filter state for the Ricerca* search views, restored on return.
 *
 * State is mirrored to the URL query string and to sessionStorage. The URL covers
 * the back button, a reload and shared links; sessionStorage covers the routes
 * that arrive without a query string, which is every in-app link (breadcrumbs,
 * header, sidenav, footer).
 *
 * @module hooks/useSearchFilterState
 */

import { debounce } from 'lodash';
import qs from 'query-string';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { hasValue, makeSeed } from '../helpers/SearchHelper';

const STORAGE_VERSION = 1;

// useLayoutEffect warns when rendered on the server, and effects never run there
// anyway, so pick the variant at module level.
const useRestoreEffect = __CLIENT__ ? useLayoutEffect : useEffect;

const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readStorage = (storageKey) => {
  if (!__CLIENT__) return null;
  try {
    const raw = window.sessionStorage.getItem(`baccanale.search.${storageKey}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeStorage = (storageKey, payload) => {
  if (!__CLIENT__) return;
  try {
    window.sessionStorage.setItem(
      `baccanale.search.${storageKey}`,
      JSON.stringify(payload),
    );
  } catch (e) {
    // Private mode or a full quota: persistence is a nicety, never a hard failure.
  }
};

const clearStorage = (storageKey) => {
  if (!__CLIENT__) return;
  try {
    window.sessionStorage.removeItem(`baccanale.search.${storageKey}`);
  } catch (e) {
    // See writeStorage.
  }
};

const positiveInt = (value) => {
  const parsed = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * A payload written by an older deploy must degrade to defaults, never reach the
 * view's filter predicates.
 */
const validateStored = (payload, knownKeys) => {
  if (!isPlainObject(payload) || payload.v !== STORAGE_VERSION) return null;
  if (!isPlainObject(payload.filter)) return null;

  const filter = {};

  Object.entries(payload.filter).forEach(([key, value]) => {
    if (!knownKeys.includes(key)) return;
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every((v) => typeof v === 'string' || typeof v === 'number')
    ) {
      filter[key] = value;
    } else if (typeof value === 'string' && value !== '') {
      filter[key] = value;
    }
  });

  const activeIndex =
    Array.isArray(payload.activeIndex) &&
    payload.activeIndex.every((index) => Number.isInteger(index))
      ? payload.activeIndex
      : null;

  return {
    filter,
    page: positiveInt(payload.page) ?? 1,
    seed: positiveInt(payload.seed),
    activeIndex,
  };
};

const serialize = (paramMap, { filter, page, seed }) => {
  const params = {};

  Object.entries(paramMap).forEach(([urlName, { key, kind }]) => {
    const value = filter[key];
    if (!hasValue(value)) return;
    // Multi-value groups become repeated params rather than a joined string:
    // option values are free text (an autore can be "Rossi, Mario") and a
    // separator would corrupt them on the way back.
    params[urlName] = kind === 'text' ? value : value.map(String);
  });

  // Nothing selected on the first page: keep the URL clean.
  if (Object.keys(params).length === 0 && page === 1) return '';

  if (page > 1) params.page = String(page);
  if (seed != null) params.seed = String(seed);

  return qs.stringify(params);
};

const deserialize = (paramMap, search) => {
  const parsed = qs.parse(search || '');
  const filter = {};
  let found = false;

  Object.entries(paramMap).forEach(([urlName, { key, kind }]) => {
    const raw = parsed[urlName];
    if (raw == null || raw === '') return;

    if (kind === 'text') {
      filter[key] = Array.isArray(raw) ? raw[0] : raw;
    } else {
      const tokens = (Array.isArray(raw) ? raw : [raw]).filter(
        (token) => token != null && token !== '',
      );
      if (tokens.length === 0) return;
      filter[key] = tokens;
    }
    found = true;
  });

  if (!found) return null;

  return {
    filter,
    page: positiveInt(parsed.page) ?? 1,
    seed: positiveInt(parsed.seed),
  };
};

/**
 * @param {Object} options Options.
 * @param {string} options.storageKey Namespace for this view's stored state.
 * @param {Object} options.paramMap { urlName: { key, kind: 'list'|'text' } }.
 * @param {Array} options.defaultActiveIndex Accordion sections open by default.
 * @param {Object} options.accordionForFilter { filterKey: accordionIndex }, used
 *   to open the sections holding restored filters.
 * @param {number} options.debounceMs Delay before state is persisted.
 * @returns {Object} Filter state and its setters.
 */
const useSearchFilterState = ({
  storageKey,
  paramMap,
  defaultActiveIndex = [],
  accordionForFilter = {},
  debounceMs = 300,
}) => {
  const location = useLocation();
  const history = useHistory();

  const [state, setState] = useState(() => ({
    ready: false,
    filter: {},
    page: 1,
    seed: null,
    activeIndex: defaultActiveIndex,
  }));

  // The restore and persist effects read these; keeping them in a ref means
  // neither effect has to re-run when a view passes fresh object literals.
  const configRef = useRef();
  configRef.current = {
    paramMap,
    storageKey,
    defaultActiveIndex,
    accordionForFilter,
  };

  const openSectionsFor = (filter) => {
    const config = configRef.current;
    const withFilters = Object.keys(filter)
      .filter(
        (key) =>
          hasValue(filter[key]) && config.accordionForFilter[key] != null,
      )
      .map((key) => config.accordionForFilter[key]);

    return Array.from(new Set([...config.defaultActiveIndex, ...withFilters]));
  };

  // Restore before paint, so a warm return never flashes an unfiltered list.
  // Render 1 stays on defaults so the server markup and the first client render
  // agree; everything environment-specific happens here.
  useRestoreEffect(() => {
    const config = configRef.current;
    const knownKeys = Object.values(config.paramMap).map(({ key }) => key);

    // A URL carrying filters wins outright — a shared link has to be
    // authoritative, and merging it with stored state would produce a
    // combination the user never chose.
    const fromUrl = deserialize(config.paramMap, location.search);
    const stored = validateStored(readStorage(config.storageKey), knownKeys);
    const resolved = fromUrl || stored;

    const filter = resolved?.filter ?? {};

    setState({
      ready: true,
      filter,
      page: resolved?.page ?? 1,
      // A hand-written link with filters but no seed must not reshuffle the
      // order a returning visitor already had.
      seed: resolved?.seed ?? stored?.seed ?? makeSeed(),
      activeIndex:
        (fromUrl ? null : stored?.activeIndex) ?? openSectionsFor(filter),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistRef = useRef();

  const persist = useMemo(
    () =>
      debounce(() => {
        const {
          paramMap: map,
          storageKey: key,
          state: current,
          pathname,
        } = persistRef.current;
        const { filter, page, seed, activeIndex } = current;

        const narrowing = Object.keys(filter).some((k) => hasValue(filter[k]));

        if (narrowing || page > 1) {
          writeStorage(key, {
            v: STORAGE_VERSION,
            filter,
            page,
            seed,
            activeIndex,
          });
        } else {
          clearStorage(key);
        }

        const search = serialize(map, current);
        const next = search ? `?${search}` : '';

        // On unmount the route has already changed, and replacing then would
        // stamp these params onto the page we navigated to.
        if (history.location.pathname !== pathname) return;
        if (history.location.search === next) return;

        history.replace({ pathname, search: next });
      }, debounceMs),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceMs],
  );

  useEffect(() => {
    persistRef.current = {
      paramMap,
      storageKey,
      state,
      pathname: location.pathname,
    };
    if (state.ready) persist();
  }, [state, paramMap, storageKey, location.pathname, persist]);

  // Typing a name and immediately clicking a card would otherwise lose the text.
  useEffect(() => () => persist.flush(), [persist]);

  const setValue = useCallback((key, value, checked) => {
    setState((prev) => {
      const current = Array.isArray(prev.filter[key]) ? prev.filter[key] : [];
      const next = checked
        ? current.concat(
            current.some((v) => String(v) === String(value)) ? [] : [value],
          )
        : current.filter((v) => String(v) !== String(value));

      const filter = { ...prev.filter };
      if (next.length > 0) {
        filter[key] = next;
      } else {
        delete filter[key];
      }

      return { ...prev, filter, page: 1 };
    });
  }, []);

  const setText = useCallback((key, value) => {
    setState((prev) => {
      const filter = { ...prev.filter };
      if (value) {
        filter[key] = value;
      } else {
        delete filter[key];
      }

      return { ...prev, filter, page: 1 };
    });
  }, []);

  const setPage = useCallback((page) => {
    setState((prev) => (prev.page === page ? prev : { ...prev, page }));
  }, []);

  const toggleAccordion = useCallback((index) => {
    setState((prev) => ({
      ...prev,
      activeIndex: prev.activeIndex.includes(index)
        ? prev.activeIndex.filter((i) => i !== index)
        : [...prev.activeIndex, index],
    }));
  }, []);

  const isChecked = useCallback(
    (key, value) => {
      const current = state.filter[key];
      return (
        Array.isArray(current) &&
        current.some((v) => String(v) === String(value))
      );
    },
    [state.filter],
  );

  const getText = useCallback((key) => state.filter[key] ?? '', [state.filter]);

  return {
    ready: state.ready,
    filter: state.filter,
    page: state.page,
    seed: state.seed,
    activeIndex: state.activeIndex,
    setValue,
    setText,
    setPage,
    toggleAccordion,
    isChecked,
    getText,
  };
};

export default useSearchFilterState;
